import '@Resources/fonts/fonts.css';
import '@Resources/styles/app.css';
import ColorsStyles from '@Styles/colors.scss';
import LayoutStyles from '@Styles/layout.scss';
import { createTheme } from '@mui/material';

declare module '@mui/material/styles' {
    interface Palette {
        indigo: Palette['secondary'];
        blue: Palette['secondary'];
        red: Palette['secondary'];
        green: Palette['secondary'];
        black: Palette['secondary'];
        gradientGrey: Palette['secondary'];
    }
    interface PaletteOptions {
        indigo: Palette['secondary'];
        blue: Palette['secondary'];
        red: Palette['secondary'];
        green: Palette['secondary'];
        black: Palette['secondary'];
        gradientGrey: Palette['secondary'];
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        black: true;
        indigo: true;
        blue: true;
        red: true;
        green: true;
    }
}

declare module '@mui/material/SvgIcon' {
    interface SvgIconPropsColorOverrides {
        gradientGrey: true;
        red: true;
    }
}

export const theme = createTheme({
    typography: {
        fontFamily: 'Barlow',
        button: {
            textTransform: 'none',
        },
    },
    palette: {
        indigo: {
            light: ColorsStyles.ikeSecondaryLightIndigo,
            main: ColorsStyles.ikeSecondaryDarkIndigo,
            dark: ColorsStyles.ikeSecondaryDarkIndigo,
            contrastText: '#fff',
        },
        blue: {
            light: ColorsStyles.ikeSecondaryLightBlue,
            main: ColorsStyles.ikeSecondaryDarkBlue,
            dark: ColorsStyles.ikeSecondaryDarkBlue,
            contrastText: '#fff',
        },
        red: {
            light: ColorsStyles.ikeSecondaryLightRed,
            main: ColorsStyles.ikeSecondaryDarkRed,
            dark: ColorsStyles.ikeSecondaryDarkRed,
            contrastText: '#fff',
        },
        green: {
            light: ColorsStyles.ikeSecondaryLightGreen,
            main: ColorsStyles.ikeSecondaryDarkGreen,
            dark: ColorsStyles.ikeSecondaryDarkGreen,
            contrastText: '#fff',
        },
        black: {
            light: ColorsStyles.ikeGradient4,
            main: ColorsStyles.ikeGradient1,
            dark: ColorsStyles.ikeGradient1,
            contrastText: '#fff',
        },
        gradientGrey: {
            light: ColorsStyles.ikeGradient5,
            main: ColorsStyles.ikeGradient5,
            dark: ColorsStyles.ikeGradient5,
            contrastText: '#fff',
        },
    },
    shape: {
        borderRadius: 0,
    },
    components: {
        MuiCard: {
            styleOverrides: {
                root: {
                    border: `1px solid ${ColorsStyles.ikeGradient11}`,
                    boxShadow: 'none',
                },
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    ':hover': {
                        boxShadow: 'none',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    ':hover': {
                        boxShadow: 'none',
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    margin: 0,
                    padding: 0,
                    '&.Mui-expanded': {
                        margin: 0,
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    position: 'inherit',
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 42,
                    height: 26,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                        padding: 0,
                        margin: 2,
                        transitionDuration: '300ms',
                        '&.Mui-checked': {
                            transform: 'translateX(16px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                                backgroundColor:
                                    ColorsStyles.ikeSecondaryLightGreen,
                                opacity: 1,
                                border: 0,
                            },
                            '&.Mui-disabled + .MuiSwitch-track': {
                                opacity: 0.5,
                            },
                        },
                        '&.Mui-focusVisible .MuiSwitch-thumb': {
                            color: ColorsStyles.ikeSecondaryLightGreen,
                            border: '6px solid #fff',
                        },
                        '&.Mui-disabled .MuiSwitch-thumb': {
                            color: ColorsStyles.ikeSecondaryLightGreen,
                        },
                        '&.Mui-disabled + .MuiSwitch-track': {
                            opacity: 0.8,
                            color: ColorsStyles.ikeGradient11,
                        },
                    },
                    '& .MuiSwitch-thumb': {
                        boxSizing: 'border-box',
                        width: 22,
                        height: 22,
                    },
                    '& .MuiSwitch-track': {
                        borderRadius: 26 / 2,
                        backgroundColor: ColorsStyles.ikeGradient11,
                        opacity: 1,
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                '@fontFace': 'Barlow',
                MuiCssBaseline: {
                    styleOverrides: {
                        body: {
                            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                                backgroundColor: ColorsStyles.ikeGradient11,
                                borderRadius: '4px',
                                padding: '2px',

                                '&:vertical': {
                                    width: LayoutStyles.scrollbarWidth,
                                },

                                '&:horizontal': {
                                    height: LayoutStyles.scrollbarWidth,
                                },
                            },
                            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb':
                                {
                                    borderRadius:
                                        LayoutStyles.scrollbarBorderRadius,
                                    backgroundColor: ColorsStyles.ikeGradient1,
                                    // border: `5px solid ${ColorVariables.greyscale60}`,
                                },
                            '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus':
                                {
                                    backgroundColor: ColorsStyles.ikeGradient1,
                                },
                            '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active':
                                {
                                    backgroundColor: ColorsStyles.ikeGradient1,
                                },
                            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover':
                                {
                                    backgroundColor: ColorsStyles.ikeGradient1,
                                },
                        },
                        textarea: {
                            fontFamily: 'Nunito Sans',
                        },
                    },
                },
            },
        },
    },
});
